// v2 dwarf shrub — a true tundra cushion: a domed central hummock ringed by flat
// windswept lobes hugging the ground, bare woody twigs poking through the mat and
// a few russet leaf tufts where autumn has caught it. Variants swap the
// green/russet balance and turn the mat. Ported 1:1 from age-of-mcp's
// v2/dwarf-shrub.ts, using the library's shared `groundcover` (h01, lerp, dirAt,
// tiltRot) helpers.
import * as THREE from "three";
import { defineModel, groundcover, type Part } from "../../lib";

const TAU = Math.PI * 2;
const TONES: [string, string, string][] = [
  ["#44643a", "#52713e", "#8a5e36"],   // green-dominant, russet accents
  ["#566a3a", "#6a763e", "#9a5d33"],   // drier mat, more autumn russet
];

function dwarfShrubParts(v: number): Part[] {
  const seed = v * 19 + 9;
  const yaw = v * 2.6 + 1.1;
  const [dark, mid, russet] = TONES[v % TONES.length]!;
  const parts: Part[] = [
    // the central hummock
    { geometry: new THREE.IcosahedronGeometry(0.16, 0), color: dark,
      position: [0, 0.1, 0], rotation: [0, yaw, 0], scale: [1.2, 0.85, 1.15] },
  ];
  // flat windswept lobes around it
  for (let i = 0; i < 4; i++) {
    const a = yaw + (i / 4) * TAU + groundcover.h01(seed, i) * 0.6;
    const r = groundcover.lerp(0.13, 0.18, groundcover.h01(seed, i + 4));
    parts.push({
      geometry: new THREE.IcosahedronGeometry(groundcover.lerp(0.085, 0.11, groundcover.h01(seed, i + 8)), 0),
      color: i % 2 ? mid : dark,
      position: [Math.cos(a) * r, groundcover.lerp(0.045, 0.06, groundcover.h01(seed, i + 12)), Math.sin(a) * r],
      rotation: [0, a, 0],
      scale: [1.3, 0.5, 1.2],
    });
  }
  // bare woody twigs poking through the mat
  for (let i = 0; i < 3; i++) {
    const a = yaw + 0.9 + (i / 3) * TAU;
    const tilt = groundcover.lerp(0.35, 0.6, groundcover.h01(seed, i + 16));
    const d = groundcover.dirAt(a, tilt);
    const bx = Math.cos(a) * 0.08, bz = Math.sin(a) * 0.08;
    const len = groundcover.lerp(0.07, 0.1, groundcover.h01(seed, i + 20));
    parts.push({
      geometry: new THREE.CylinderGeometry(0.004, 0.007, len, 4),
      color: "#6b4a30",
      position: [bx + d[0] * len * 0.5, 0.1 + d[1] * len * 0.5, bz + d[2] * len * 0.5],
      rotation: groundcover.tiltRot(a, tilt),
    });
  }
  // russet leaf tufts caught in the cushion
  for (let i = 0; i < 3; i++) {
    const a = yaw + 0.4 + (i / 3) * TAU + groundcover.h01(seed, i + 24) * 0.8;
    const r = groundcover.lerp(0.05, 0.13, groundcover.h01(seed, i + 28));
    parts.push({
      geometry: new THREE.IcosahedronGeometry(0.028, 0), color: russet,
      position: [Math.cos(a) * r, groundcover.lerp(0.1, 0.15, groundcover.h01(seed, i + 32)), Math.sin(a) * r],
      scale: [1.1, 0.6, 1.0],
    });
  }
  return parts;
}

export const dwarfShrubV2 = defineModel({
  id: "dwarf-shrub-v2",
  batchId: "fable",
  name: "Dwarf shrub (v2)",
  tags: ["scenery","dwarf","shrub","low-poly","age-of-mcp","fable"],
  summary: "Low-poly dwarf shrub.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.3,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => dwarfShrubParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => dwarfShrubParts(1) },
  ],
  animations: [],
});
